// src/context/AuthProvider.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios'; // Assuming axios is used for API calls
import PropTypes from 'prop-types'; // For prop type validation
import AuthContext from './AuthContext'; // Import the context we just created
// Import all relevant backend URLs from your config
import {
    NODE_FLASK_LOG_URL,         // The specific login URL (e.g., https://.../flaskapi/auth/login)
    NODE_BACKEND_API_URL,       // Base for /apitest endpoints (e.g., https://.../apitest)
    NODE_BACKEND_PRD_URL,       // Base for /api/v3/ endpoints (e.g., https://.../api/v3/)
    NODE_INVOICE_API_URL,       // Base for /api endpoints (e.g., https://.../api)
    FLASK_BACKEND_API_URL       // Base for /api/fileupload/ endpoints (e.g., https://.../api/fileupload/)
    // NODE_BACKEND_SCH_URL is covered by NODE_BACKEND_PRD_URL if it's under /api/v3/
} from '../config';


// --- Constants ---
const LOCAL_STORAGE_ACCESS_TOKEN_KEY = 'authAccessToken'; // Access token (30 min)
const LOCAL_STORAGE_REFRESH_TOKEN_KEY = 'authRefreshToken'; // Refresh token (7 days)
const LOCAL_STORAGE_USER_KEY = 'authUser'; // User details
const LOCAL_STORAGE_TOKEN_KEY = 'authToken'; // Legacy support

// LANE B: proactive refresh leeway + tick cadence.
// 5 min before the JWT's `exp` is the earliest we'll proactively refresh;
// check every 30 s on a setInterval. The interceptor (line 390+) still handles
// reactive refresh on any 401, so this is purely belt-and-braces.
const PROACTIVE_REFRESH_LEEWAY_MS = 5 * 60 * 1000;
const PROACTIVE_REFRESH_TICK_MS = 30 * 1000;

// Read-only JWT `exp` decoder (no signature verification — we only inspect
// the claim locally; the server re-validates on next request).
function decodeJwtExp(token) {
    if (!token || typeof token !== 'string') return null;
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;
        const padded = payload + '==='.slice((payload.length + 3) % 4);
        const obj = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
        return typeof obj.exp === 'number' ? obj.exp * 1000 : null;
    } catch {
        return null;
    }
}

const AuthProvider = ({ children }) => {
    // --- State for Authentication ---
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null); // Stores user object
    const [accessToken, setAccessToken] = useState(null); // Access token (30 min)
    const [refreshToken, setRefreshToken] = useState(null); // Refresh token (7 days)
    const [token, setToken] = useState(null); // Legacy support
    const [isLoadingAuth, setIsLoadingAuth] = useState(true); // Loading state
    const [isRefreshing, setIsRefreshing] = useState(false); // Prevent concurrent refreshes
    const [interceptorsReady, setInterceptorsReady] = useState(false); // Track when interceptors are set up
    
    // --- Ref to store the refresh promise so multiple requests can wait on it ---
    const refreshPromiseRef = useRef(null);

    // --- Helper to clear all auth state ---
    const clearAuthState = useCallback(() => {
        localStorage.removeItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);
        localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
        localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
        localStorage.removeItem(LOCAL_STORAGE_USER_KEY);
        setAccessToken(null);
        setRefreshToken(null);
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
    }, []);

    // --- Helper function to load auth state from localStorage ---
    const loadAuthStateFromLocalStorage = useCallback(() => {
        console.log("Attempting to load auth state from localStorage...");
        try {
            // Try new token format first
            let savedAccessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);
            let savedRefreshToken = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
            
            // Fallback to legacy token for backward compatibility
            if (!savedAccessToken) {
                savedAccessToken = localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
            }
            
            const savedUser = localStorage.getItem(LOCAL_STORAGE_USER_KEY);

            if (savedAccessToken && savedUser) {
                const parsedUser = JSON.parse(savedUser);
                // Basic validation
                if (parsedUser && typeof parsedUser.userId !== 'undefined' && 
                    typeof parsedUser.organizationId !== 'undefined' && 
                    typeof parsedUser.role !== 'undefined' && 
                    typeof parsedUser.clientDatabaseName !== 'undefined') {
                    console.log("Loaded auth state from localStorage.");
                    setAccessToken(savedAccessToken);
                    setRefreshToken(savedRefreshToken);
                    setToken(savedAccessToken); // Legacy
                    setUser(parsedUser);
                    setIsAuthenticated(true);
                } else {
                    console.warn("Invalid user data in localStorage. Clearing auth state.");
                    clearAuthState();
                }
            } else {
                console.log("No auth state found in localStorage.");
                clearAuthState();
            }
        } catch (e) {
            console.error("Error loading auth state from localStorage:", e);
            clearAuthState();
        }
        // Note: Don't set isLoadingAuth to false here - wait until interceptors are ready
    }, [clearAuthState]); // Depend on clearAuthState

    // --- Effect to load auth state on provider mount ---
    useEffect(() => {
        loadAuthStateFromLocalStorage();

        // Optional: Add a listener for storage events to sync auth state across tabs/windows
        const handleStorageChange = (event) => {
            if (event.key === LOCAL_STORAGE_TOKEN_KEY || event.key === LOCAL_STORAGE_USER_KEY) {
                console.log("Storage event detected, reloading auth state.");
                loadAuthStateFromLocalStorage(); // Reload state if token or user changes in other tabs
            }
        };
        window.addEventListener('storage', handleStorageChange);

        // Cleanup listener on unmount
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [loadAuthStateFromLocalStorage]); // Depend on the memoized callback


    // --- Login Function ---
    // This function will be called from the login page
    const login = useCallback(async (credentials) => {
        console.log("Attempting login with credentials:", credentials);
        setIsLoadingAuth(true); // Indicate loading state during login attempt
        try {
            // Use the correct Flask login URL from config
            const response = await axios.post(NODE_FLASK_LOG_URL, credentials, {
                timeout: 20000
            });

            // Check for both new (accessToken) and legacy (token) formats
            if (response.data && (response.data.accessToken || response.data.token)) {
                const receivedAccessToken = response.data.accessToken || response.data.token;
                const receivedRefreshToken = response.data.refreshToken;
                const receivedUser = {
                    ...(response.data.user || {}),
                    signageEnabled: !!(response.data.signageEnabled ?? response.data.user?.signageEnabled),
                    totemEnabled: !!(response.data.totemEnabled ?? response.data.user?.totemEnabled),
                    afipEnabled: !!(response.data.afipEnabled ?? response.data.user?.afipEnabled),
                    a4InvoiceEnabled: !!(response.data.a4InvoiceEnabled ?? response.data.user?.a4InvoiceEnabled),
                    priceRoundingStep: Number(response.data.priceRoundingStep ?? response.data.user?.priceRoundingStep) || 0,
                    creditCardMarkupEnabled: !!(response.data.creditCardMarkupEnabled ?? response.data.user?.creditCardMarkupEnabled),
                    brandScopedPriceRounding: !!(response.data.brandScopedPriceRounding ?? response.data.user?.brandScopedPriceRounding),
                    hideProductCodeColumn: !!(response.data.hideProductCodeColumn ?? response.data.user?.hideProductCodeColumn),
                    hideCalculationValidationBadge: !!(response.data.hideCalculationValidationBadge ?? response.data.user?.hideCalculationValidationBadge),
                };

                console.log("Login successful. Received tokens.");

                // Save to state
                setAccessToken(receivedAccessToken);
                setRefreshToken(receivedRefreshToken);
                setToken(receivedAccessToken); // Legacy
                setUser(receivedUser);
                setIsAuthenticated(true);

                // Save to localStorage
                localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY, receivedAccessToken);
                if (receivedRefreshToken) {
                    localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY, receivedRefreshToken);
                }
                localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, receivedAccessToken); // Legacy
                if (response.data.user) {
                    localStorage.setItem(LOCAL_STORAGE_USER_KEY, JSON.stringify(receivedUser));
                }

                // Trigger storage event
                window.dispatchEvent(new Event('storage'));

                setIsLoadingAuth(false);
                return { success: true, user: receivedUser };
            } else {
                console.warn("Login failed: Invalid response data format.", response.data);
                clearAuthState();
                setIsLoadingAuth(false);
                return { success: false, message: "Invalid response from authentication server." };
            }
        } catch (error) {
            console.error('Login failed:', error);
            clearAuthState();
            setIsLoadingAuth(false);

            // Return specific error message from backend if available, otherwise a generic one
            const errorMessage = error.response && error.response.data && (error.response.data.error || error.response.data.message) // Check for 'error' or 'message'
                ? (error.response.data.error || error.response.data.message)
                : "Login failed. Please check your credentials.";

            return { success: false, message: errorMessage }; // Indicate failure and return error message
        }
    }, [clearAuthState]); // Depend on clearAuthState

    // --- Refresh Access Token Function ---
    // Uses a promise ref so multiple simultaneous 401s can wait for the same refresh
    const refreshAccessToken = useCallback(async () => {
        console.log("[Auth Refresh] Attempting to refresh access token...");
        
        // CRITICAL: Check promise ref FIRST (synchronous) - if a refresh is in progress, wait for it
        if (refreshPromiseRef.current) {
            console.log("[Auth Refresh] Refresh already in progress, waiting for existing refresh...");
            try {
                const result = await refreshPromiseRef.current;
                console.log("[Auth Refresh] Existing refresh completed, got token:", !!result);
                return result;
            } catch (error) {
                console.log("[Auth Refresh] Existing refresh failed:", error.message);
                return null;
            }
        }

        const currentRefreshToken = refreshToken || localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
        console.log("[Auth Refresh] Refresh token exists:", !!currentRefreshToken);
        
        if (!currentRefreshToken) {
            console.error("[Auth Refresh] No refresh token available - clearing auth state");
            clearAuthState();
            return null;
        }

        setIsRefreshing(true);
        
        // Create the refresh promise and store it so other requests can wait on it
        const refreshPromise = (async () => {
            try {
                const refreshUrl = `${NODE_FLASK_LOG_URL.replace('/login', '/refresh')}`;
                console.log("[Auth Refresh] Calling refresh endpoint:", refreshUrl);
                
                const response = await axios.post(
                    refreshUrl,
                    { 
                        refreshToken: currentRefreshToken,
                        rotateToken: false // Set to true for token rotation
                    },
                    { timeout: 10000 }
                );
                
                console.log("[Auth Refresh] Response status:", response.status);
                console.log("[Auth Refresh] Response has accessToken:", !!response.data?.accessToken);
                console.log("[Auth Refresh] Response has token:", !!response.data?.token);

                if (response.data && (response.data.accessToken || response.data.token)) {
                    const newAccessToken = response.data.accessToken || response.data.token;
                    const newRefreshToken = response.data.refreshToken; // If rotated
                    const updatedUser = {
                        ...(response.data.user || user),
                        signageEnabled: response.data.signageEnabled !== undefined
                            ? !!response.data.signageEnabled
                            : (response.data.user || user)?.signageEnabled,
                        totemEnabled: response.data.totemEnabled !== undefined
                            ? !!response.data.totemEnabled
                            : (response.data.user || user)?.totemEnabled,
                        afipEnabled: response.data.afipEnabled !== undefined
                            ? !!response.data.afipEnabled
                            : (response.data.user || user)?.afipEnabled,
                        a4InvoiceEnabled: response.data.a4InvoiceEnabled !== undefined
                            ? !!response.data.a4InvoiceEnabled
                            : (response.data.user || user)?.a4InvoiceEnabled,
                        priceRoundingStep: response.data.priceRoundingStep !== undefined
                            ? Number(response.data.priceRoundingStep) || 0
                            : (response.data.user || user)?.priceRoundingStep || 0,
                        creditCardMarkupEnabled: response.data.creditCardMarkupEnabled !== undefined
                            ? !!response.data.creditCardMarkupEnabled
                            : (response.data.user || user)?.creditCardMarkupEnabled,
                        brandScopedPriceRounding: response.data.brandScopedPriceRounding !== undefined
                            ? !!response.data.brandScopedPriceRounding
                            : (response.data.user || user)?.brandScopedPriceRounding,
                        hideProductCodeColumn: response.data.hideProductCodeColumn !== undefined
                            ? !!response.data.hideProductCodeColumn
                            : (response.data.user || user)?.hideProductCodeColumn,
                        hideCalculationValidationBadge: response.data.hideCalculationValidationBadge !== undefined
                            ? !!response.data.hideCalculationValidationBadge
                            : (response.data.user || user)?.hideCalculationValidationBadge,
                    };

                    console.log("[Auth Refresh] ✅ Token refresh successful!");
                    console.log("[Auth Refresh] New access token prefix:", newAccessToken?.substring(0, 20) + '...');
                    console.log("[Auth Refresh] Got new refresh token:", !!newRefreshToken);

                    // Update state
                    setAccessToken(newAccessToken);
                    setToken(newAccessToken); // Legacy
                    if (newRefreshToken) {
                        setRefreshToken(newRefreshToken);
                    }
                    if (updatedUser) {
                        setUser(updatedUser);
                    }

                    // Update localStorage
                    localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY, newAccessToken);
                    localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, newAccessToken); // Legacy
                    if (newRefreshToken) {
                        localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY, newRefreshToken);
                    }
                    if (updatedUser) {
                        localStorage.setItem(LOCAL_STORAGE_USER_KEY, JSON.stringify(updatedUser));
                    }

                    return newAccessToken;
                } else {
                    console.error("[Auth Refresh] ❌ Token refresh failed: Invalid response format");
                    console.error("[Auth Refresh] Response data:", JSON.stringify(response.data));
                    clearAuthState();
                    throw new Error("Invalid response format");
                }
            } catch (error) {
                console.error('[Auth Refresh] ❌ Token refresh request failed:', error.message);
                console.error('[Auth Refresh] Error status:', error.response?.status);
                console.error('[Auth Refresh] Error data:', error.response?.data);
                
                // If refresh fails with 401, clear auth and force re-login
                if (error.response?.status === 401) {
                    console.log("[Auth Refresh] Refresh token expired or invalid (401). Logging out.");
                    clearAuthState();
                } else {
                    console.log("[Auth Refresh] Non-401 error, not clearing auth state");
                }
                
                throw error;
            }
        })();
        
        // Store the promise so other requests can wait on it
        refreshPromiseRef.current = refreshPromise;
        
        try {
            const result = await refreshPromise;
            return result;
        } catch (error) {
            return null;
        } finally {
            setIsRefreshing(false);
            refreshPromiseRef.current = null;
        }
    }, [refreshToken, isRefreshing, user, clearAuthState]);

    // --- LANE B: Proactive refresh timer ---
    // The 401-driven interceptor (line ~390) handles refreshes triggered by API
    // calls, but when a kiosk (or any idle tab) has no API traffic for >30 min,
    // the access token expires silently in localStorage. On the next call after
    // expiry, the interceptor fires a reactive refresh — but if the refresh
    // token has also lapsed (7 d), the user gets logged out. This timer
    // refreshes BEFORE expiry, keeping the sliding window alive without human
    // intervention.
    //
    // Tick every 30 s; refresh when access-token `exp` is within 5 min.
    useEffect(() => {
        if (!isAuthenticated) return undefined;
        const handle = setInterval(async () => {
            const access =
                localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY) ||
                localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
            const refresh = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
            if (!access) return;
            const exp = decodeJwtExp(access);
            if (!exp) return;
            const willExpireSoon = Date.now() + PROACTIVE_REFRESH_LEEWAY_MS >= exp;
            if (willExpireSoon && refresh) {
                console.log('[Auth Refresh] Proactive refresh (within',
                    Math.round((exp - Date.now()) / 1000), 's of expiry)');
                await refreshAccessToken();
            }
        }, PROACTIVE_REFRESH_TICK_MS);
        return () => clearInterval(handle);
    }, [isAuthenticated]);

    // --- Logout Function ---
    // This function will be called from anywhere the user logs out
    const logout = useCallback(async (revokeAll = false) => {
        console.log("Logging out...");
        
        const currentRefreshToken = refreshToken || localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
        
        // Call backend logout to revoke refresh token
        if (currentRefreshToken) {
            try {
                await axios.post(
                    `${NODE_FLASK_LOG_URL.replace('/login', '/logout')}`,
                    { 
                        refreshToken: currentRefreshToken,
                        revokeAll: revokeAll 
                    },
                    { 
                        timeout: 5000,
                        headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}
                    }
                );
                console.log("Server-side logout successful.");
            } catch (error) {
                console.error("Server-side logout failed:", error);
                // Continue with client-side logout even if server call fails
            }
        }

        // Clear all auth state
        clearAuthState();

        // Trigger storage event
        window.dispatchEvent(new Event('storage'));

        console.log("Logged out successfully.");
    }, [refreshToken, accessToken, clearAuthState]);

    // --- Axios Interceptor Setup ---
    // This effect runs once on mount to configure axios
    useEffect(() => {
        // Request interceptor - add access token to requests
        const requestInterceptor = axios.interceptors.request.use(
            (config) => {
                console.log('[Axios Request Interceptor] URL:', config.url);
                
                // Skip if this is a retry with a fresh token already set
                if (config._hasRefreshedToken) {
                    console.log('[Axios Request Interceptor] Skipping - retry with fresh token');
                    return config;
                }
                
                // ALWAYS read from localStorage for the freshest token
                const currentAccessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);

                // Determine if the request URL is for authenticated backend APIs
                const isForAuthenticatedBackend =
                    config.url.startsWith(NODE_BACKEND_API_URL) ||     // /apitest/...
                    config.url.startsWith(NODE_BACKEND_PRD_URL) ||     // /api/v3/...
                    config.url.startsWith(NODE_INVOICE_API_URL) ||     // /api/...
                    config.url.startsWith(FLASK_BACKEND_API_URL);      // /api/fileupload/...

                // Exclude login and refresh endpoints
                // Note: must check for auth-specific paths only — avoid matching e.g. /pack-products/refresh
                const isLoginOrRefreshUrl = config.url.includes('/auth/login') ||
                    config.url.includes('/auth/refresh') ||
                    config.url.includes('/flaskapi/auth/');

                // Add Authorization header for authenticated requests
                if (currentAccessToken && isForAuthenticatedBackend && !isLoginOrRefreshUrl) {
                    console.log('[Axios Request Interceptor] Adding Authorization header');
                    config.headers.Authorization = `Bearer ${currentAccessToken}`;
                }

                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor - handle 401 errors with automatic refresh (up to 3 attempts)
        const responseInterceptor = axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;
                
                console.log('[Axios Response Interceptor] Error detected:', {
                    status: error.response?.status,
                    url: originalRequest?.url,
                    retryCount: originalRequest?._retryCount || 0
                });

                // Initialize retry counter if not present
                if (!originalRequest._retryCount) {
                    originalRequest._retryCount = 0;
                }

                // Check if error is 401 and we haven't exceeded retry limit (3 attempts total)
                if (error.response?.status === 401 && originalRequest._retryCount < 3) {
                    // Don't auto-refresh for login/refresh endpoints themselves
                    const isLoginOrRefreshUrl = originalRequest.url?.includes('/login') || 
                                              originalRequest.url?.includes('/refresh');
                    
                    if (!isLoginOrRefreshUrl) {
                        originalRequest._retryCount++;
                        console.log(`[Auth] Access token expired (401), attempting refresh (attempt ${originalRequest._retryCount}/3)...`);

                        try {
                            const newAccessToken = await refreshAccessToken();
                            
                            if (newAccessToken) {
                                // Mark as having a fresh token so request interceptor doesn't overwrite
                                originalRequest._hasRefreshedToken = true;
                                originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                                console.log(`[Auth] Token refreshed, retrying request (attempt ${originalRequest._retryCount}/3)...`);
                                return axios(originalRequest);
                            } else {
                                // Refresh failed, redirect to login
                                console.log(`[Auth] Token refresh failed after ${originalRequest._retryCount} attempt(s), redirecting to login...`);
                                clearAuthState();
                                window.location.href = '/login';
                                return Promise.reject(error);
                            }
                        } catch (refreshError) {
                            console.error(`[Auth] Error during token refresh (attempt ${originalRequest._retryCount}/3):`, refreshError);
                            
                            // If we haven't hit retry limit, the next 401 will trigger another attempt
                            if (originalRequest._retryCount >= 3) {
                                console.log(`[Auth] Max retry attempts (3) exceeded, logging out...`);
                                clearAuthState();
                                window.location.href = '/login';
                            }
                            return Promise.reject(refreshError);
                        }
                    } else {
                        // Login/refresh endpoints failed - force logout
                        console.log("[Auth] Authentication endpoint error, logging out...");
                        clearAuthState();
                        window.location.href = '/login';
                    }
                } else if (error.response?.status === 401 && originalRequest._retryCount >= 3) {
                    // Exceeded retry limit
                    console.log(`[Auth] Max retry attempts (3) exceeded for request, logging out...`);
                    clearAuthState();
                    window.location.href = '/login';
                }

                return Promise.reject(error);
            }
        );

        // Mark interceptors as ready and auth as loaded
        // Use setTimeout to ensure interceptors are fully registered before allowing rendering
        setTimeout(() => {
            setInterceptorsReady(true);
            setIsLoadingAuth(false);
            console.log("[AuthProvider] Interceptors are now ready and auth state loaded");
        }, 0);

        // Cleanup interceptors on unmount
        return () => {
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, [accessToken, refreshAccessToken, clearAuthState]);


    // --- Context Value ---
    // Memoize the context value to prevent unnecessary re-renders of consumers
    const contextValue = useMemo(() => ({
        isAuthenticated,
        user,
        token, // Legacy support
        accessToken,
        refreshToken,
        isLoadingAuth,
        isRefreshing,
        interceptorsReady,
        login, // Provide the login function
        logout, // Provide the logout function
        refreshAccessToken, // Expose for manual refresh if needed
    }), [isAuthenticated, user, token, accessToken, refreshToken, isLoadingAuth, isRefreshing, interceptorsReady, login, logout, refreshAccessToken]);


    // --- Render Provider ---
    // While loading the initial auth state, you might render a loading spinner
    // or null to prevent rendering the application before the state is known.
    // For now, we'll render children immediately, but you can add a check here:
    // if (isLoadingAuth) { return <div>Loading...</div>; }

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

// Add PropTypes for children
AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export default AuthProvider;